import { analyticsTrack, comscoreTrack, DEFAULT_TRACKING_EVENT } from './analytics-track';
import { advOutbrainShowHide, advRefresh } from './adv-utils';
import { urlUpdate } from './urls';
import { setDigitalData } from './digital-data';
import { httpGetHTML } from './http-get-html';
import { dynamicParselyTrackPageView } from './parsely-tracking';
import { trackIntcmp, composeIntcmp, hydrateIntcmp } from './intcmp-tracking';
import { trackComponentsInTabs } from './track-web-components';
import { prepareImagesForNativeLazyLoading } from './dom-utils';
import { getActiveTab } from './get-active-tab';
import {
  ACTIVE_TAB_CLASS_PREFIX,
  CSS_PREFIX,
  HERO_HOOK,
  HERO_SLIM_HOOK,
  HERO_SLIM_IS_VISIBLE,
  LOCAL_NAV_EL_SELECTOR,
  SECTION_NAV_EL_SELECTOR,
  TAB_ITEM_CLASS_PREFIX,
  TAB_SWITCH_CONTENT_ID,
  TABS_MENU_CLASSNAME,
} from './constants';
import { tabScrollIntoView } from './tab-scroll-into-view';
import { handleLMPHeadings } from './handle-lmp-headings';

/* Events*/
const LMP_MENU_ITEM_CLICKED_EVENT = 'lmp_menu_item_clicked';

export const hydrateTabsMenu = (): void => {
  const menu: HTMLElement | null = document.querySelector(`.${CSS_PREFIX}${TABS_MENU_CLASSNAME}:not(.pp-menu-tabs)`);

  if (menu?.dataset?.section === 'lgp') {
    return;
  }

  if (menu) {
    const menuItems = Array.from(menu.children);
    menuItems.forEach((tab) => {
      tabAddClickListener(menu, tab);
    });

    fetchTabsInIDLE();
    advOutbrainShowHide(getActiveTab());
  }
  trackComponentsInTabs();
  handleLMPHeadings();
};

const tabAddClickListener = (menu: Element, tab: Element): void => {
  const dataUrl: string | null = tab.getAttribute('data-url') || '';
  const dataTabUrl: string | null = tab.getAttribute('data-tab-url');
  const dataTracking: string | null = tab.getAttribute('data-tracking') || '';
  const tabText: string = tab.getElementsByTagName('span')[0]?.innerHTML.toLocaleLowerCase(); // TEXT in the tab
  const id: string = tab.getAttribute('id')!.replace(`${CSS_PREFIX}${TAB_ITEM_CLASS_PREFIX}`, '') || '';
  /** custom click event to dispatch */
  const clickEvent = new CustomEvent(LMP_MENU_ITEM_CLICKED_EVENT, {
    bubbles: true,
    detail: { dataTabUrl, dataUrl, dataTracking, tabText, id },
  });

  tab.addEventListener('click', (e) => {
    // Center selected TAB into viewport
    // tabScrollIntoView(tab, isSticky(document.querySelector(HERO_SLIM_HOOK)) ? 'auto' : 'smooth'); // When HERO is STICKY "smooth" doesn't work
    setTimeout(() => tabScrollIntoView(tab), isSticky(document.querySelector(HERO_SLIM_HOOK)) ? 100 : 0); // When HERO is STICKY tabScrollIntoView() doesn't work without setTimeout()

    const tabContent: HTMLElement | null = document.getElementById(`${CSS_PREFIX}${TAB_SWITCH_CONTENT_ID}-${id}`);
    e.target?.dispatchEvent(clickEvent);

    if (tabContent?.children.length && id) {
      switchTab(menu, id, dataUrl, dataTracking, tabContent, tabText);
    } else if (tabContent && dataTabUrl && id) {
      console.warn('[hydrate-tabs-menu.ts] fetch tabs HTML', dataTabUrl); // eslint-disable-line no-console
      httpGetHTML(dataTabUrl)
        .then(({ data, status }) => {
          if (status === 200) {
            tabContent.innerHTML = data;
            prepareImagesForNativeLazyLoading(tabContent);
            switchTab(menu, id, dataUrl, dataTracking, tabContent, tabText);
            hydrateIntcmp(tabContent);
          } else {
            console.error('error response status ==! 200 tab not enabled!'); // eslint-disable-line no-console
          }
        })
        .catch((error) => console.error(error)); // eslint-disable-line no-console
    }

    // ISS-5178
    advOutbrainShowHide(tab);
  });
};

/**
 * Switch tab on Menu + Tracking and advRefresh()
 * @param menu Menu element for tab focus
 * @param id id of the elements
 * @param dataUrl url to set in history
 * @param dataTracking data for tracking
 */
const switchTab = (
  menu: Element | null,
  id: string,
  dataUrl = '',
  dataTracking = '',
  tabContent: HTMLElement,
  tabText: string
) => {
  menu && menu.setAttribute('data-state', `${ACTIVE_TAB_CLASS_PREFIX}${id}`);
  anchorTabOnTop();
  dynamicParselyTrackPageView({ url: `${location.origin}${dataUrl}`, urlRef: location.href });
  urlUpdate(dataUrl, history);
  handleLMPHeadings();
  setDigitalData(dataTracking, { intcmp: composeIntcmp(tabText), quartoLivello: tabText });
  trackIntcmp(tabText);
  analyticsTrack(DEFAULT_TRACKING_EVENT, dataTracking)
    .then((value) => console.log('[analytics-track.ts]', DEFAULT_TRACKING_EVENT, value)) // eslint-disable-line no-console
    .catch((err) => console.warn('[analytics-track.ts]', DEFAULT_TRACKING_EVENT, err)); // eslint-disable-line no-console
  comscoreTrack(id);
  trackComponentsInTabs();

  /**
   * As of today, this function causes an error on the page due to _4SDexRefresh,
   * so make sure to keep it at the end.
   */
  advRefresh();
};

/**
 * TABS IDLE MANAGENT
 * fetch and hydrate tabs in IDLE
 */
const fetchTabsInIDLE = () => {
  if ('requestIdleCallback' in window) {
    // @ts-ignore
    window.requestIdleCallback(hydrateEmptyTabs);
  } else {
    console.warn('[hydrate-tabs-menu.ts] fetch tabs HTML requestIdleCallback not supported'); // eslint-disable-line no-console
  }
};

/**
 * Hydrate all empty tabs
 */
const hydrateEmptyTabs = () => {
  const menu: Element | null = document.getElementsByClassName(`${CSS_PREFIX}${TABS_MENU_CLASSNAME}`)[0];
  const menuItems: HTMLCollection = menu.children;
  Array.from(menuItems).forEach((tab: Element) => {
    const dataTabUrl = tab.getAttribute('data-tab-url');
    const id = tab.getAttribute('id')?.replace(`${CSS_PREFIX}${TAB_ITEM_CLASS_PREFIX}`, '');
    const tabContent = document.getElementById(`${CSS_PREFIX}${TAB_SWITCH_CONTENT_ID}-${id}`);

    if (tabContent && tabContent?.children.length === 0 && dataTabUrl) {
      httpGetHTML(dataTabUrl)
        .then(({ data, status }) => {
          if (status === 200) {
            tabContent.innerHTML = data;
            prepareImagesForNativeLazyLoading(tabContent);
            hydrateIntcmp(tabContent);
            handleLMPHeadings();
            console.info('[hydrate-tabs-menu.ts] tab hydrated: ', { id, dataTabUrl, tabContent, data }); // eslint-disable-line no-console
          } else {
            console.error('[hydrate-tabs-menu.ts] error response status ==! 200 tab not enabled!'); // eslint-disable-line no-console
          }
        })
        .catch((error) => console.error(error)); // eslint-disable-line no-console
    }
  });
};

/**
 * Is HERO SLIM/STICKY visible in this moment?
 * @param heroSlimEl Hero Slim HTML Element
 * @returns boolean
 */
const isSticky = (heroSlimEl: HTMLElement | null): boolean =>
  heroSlimEl ? heroSlimEl.getAttribute('data-state') === HERO_SLIM_IS_VISIBLE : false;

/**
 * Utility function to scroll to top of the tab
 * @param tabDiv tab div element
 */
const anchorTabOnTop = () => {
  const heroEl: HTMLElement | null = document.querySelector(HERO_HOOK);
  const heroSlimEl: HTMLElement | null = document.querySelector(HERO_SLIM_HOOK);

  if (heroEl && heroSlimEl && isSticky(heroSlimEl)) {
    const localNavElHeight = Number(document.querySelector(LOCAL_NAV_EL_SELECTOR)?.getBoundingClientRect().height);
    const sectionNavElHeight = Number(document.querySelector(SECTION_NAV_EL_SELECTOR)?.getBoundingClientRect().height);
    const heroSlimElHeight = heroSlimEl.getBoundingClientRect().height;

    const offset = localNavElHeight + sectionNavElHeight + heroSlimElHeight - 2;
    window.scrollTo({ top: heroEl.getBoundingClientRect().bottom + window.scrollY - offset });
  }
};

// ISS-5096
export const clickFromHash = () => {
  // Get #HASH of CURRENT URL
  const hash: string = getHash();

  if (hash !== '') {
    // CLICK on TAB with DATA-URL=#HASH
    const tab: HTMLElement | null = document.querySelector(`.ftbl__tab-item[data-url="${hash}`);
    if (tab) {
      tab.click();
    } else {
      console.warn('Tab with DATA-URL', hash, 'not found'); // eslint-disable-line no-console
    }
  }
};

export const getHash = (): string => {
  return window!.location!.hash ?? '';
};
